import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useApiService } from '../services/useApiService';
import LoadingSpinner from '../components/LoadingSpinner';

function EmailVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const { onboarding } = useApiService();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    let mounted = true;
    let countdownTimer;
    let verificationAttempted = false;

    const verifyEmail = async () => {
      if (verificationAttempted) return;
      verificationAttempted = true;

      const params = new URLSearchParams(location.search);
      const token = params.get('token');

      if (!token || token.length < 32) {
        setStatus('error');
        setError('Invalid verification link');
        return;
      }

      try {
        const cleanToken = token.trim().replace(/\s+/g, '');
        await onboarding.verifyEmail(cleanToken);

        if (!mounted) return;

        setStatus('success');
        // Start countdown
        countdownTimer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownTimer);
              navigate('/login');
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } catch (error) {
        if (!mounted) return;
        
        // Check specific error codes from the server
        const errorCode = error.response?.data?.code;
        let errorMessage = error.response?.data?.error;
        if (!errorMessage || /status code|failed to verify/i.test(errorMessage)) {
          errorMessage = 'This verification link is invalid or has expired. Request a new email and try again.';
        }
        
        // If already verified, treat as success
        if (errorCode === 'ALREADY_VERIFIED') {
          setStatus('success');
          countdownTimer = setInterval(() => {
            setCountdown(prev => {
              if (prev <= 1) {
                clearInterval(countdownTimer);
                navigate('/login');
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
          return;
        }

        setStatus('error');
        setError(errorMessage);
      }
    };

    verifyEmail();

    return () => {
      mounted = false;
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
    };
  }, [location, onboarding, navigate]);

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Verifying your email...</h2>
            <LoadingSpinner size="large" />
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Email Verified!</h2>
            <p className="text-sm text-gray-600 mb-4">
              Redirecting to login in {countdown} seconds...
            </p>
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-900 hover:bg-gray-800"
            >
              Go to Login
            </button>
          </div>
        );

      case 'error':
        return (
          <div className="rounded-md bg-red-50 p-4">
            <h3 className="text-sm font-medium text-red-800">Verification Failed</h3>
            <p className="mt-2 text-sm text-red-700">{error}</p>
            <div className="mt-4">
              <Link to="/resend-verification" className="text-sm font-medium text-red-600 hover:text-red-500">
                Request new verification email
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8">
        {renderContent()}
      </div>
    </div>
  );
}

export default EmailVerification;
